import { Link } from 'react-router-dom';
import {
  Mic,
  Stethoscope,
  Globe,
  Megaphone,
  PhoneCall,
  ClipboardList,
  Truck,
  TrendingUp,
  Users,
  ArrowRight,
  X,
  Check
} from 'lucide-react';

const PILLARS = [
  {
    icon: Mic,
    title: 'Voice AI Intake',
    body:
      'Alfred Voice AI completes the clinical intake in natural conversation — English or Spanish. No forms. No web portal. A 5-minute callback that converts at ~100× the rate of a 30-minute form-driven follow-up.'
  },
  {
    icon: Stethoscope,
    title: 'Senior Clinical Governance',
    body:
      'Standards-of-care and prescribing protocols set by credentialed physicians — not rented networks. The regulatory answer to FDA scrutiny in the GLP-1 category, anchored by Dr. Ganesh Shidham (Nephrology · OSU Wexner).'
  },
  {
    icon: Globe,
    title: 'Demographic Strategy',
    body:
      'Purpose-built for the 25–30M underserved adults with metabolic burden and structural access friction. Spanish-language voice is native — not a translation layer added on top.'
  }
];

const PRODUCT_STEPS = [
  {
    n: '01',
    icon: Megaphone,
    title: 'See the ad or hear the podcast',
    body:
      'A Spanish-language YouTube ad, a WhatsApp community post, or an episode of the VitaMD Podcast. The patient hears a doctor they trust speak their language.'
  },
  {
    n: '02',
    icon: PhoneCall,
    title: 'Talk to Alfred',
    body:
      'Patient visits vitamd.health or calls the number. Alfred Voice AI completes the intake in natural conversation. English or Spanish. 5 minutes. No forms.'
  },
  {
    n: '03',
    icon: ClipboardList,
    title: 'Physician review',
    body:
      'Intake data routes to a licensed physician for clinical review. Senior governance standards apply. Only qualified patients proceed.'
  },
  {
    n: '04',
    icon: Truck,
    title: 'Program begins',
    body:
      'Prescription issued. Branded GLP-1 medication shipped from a LegitScript-certified pharmacy. Monthly check-in calls from Alfred. Retention built into the product.'
  },
  {
    n: '05',
    icon: TrendingUp,
    title: 'Platform expands',
    body:
      'Weight-loss patients cross-sell to integrated metabolic care. Men’s-specific care (TRT, ED) launches in Phase 2 — one relationship across verticals.'
  }
];

const WHY_NOW = [
  {
    n: '01',
    title: 'GLP-1 demand has no ceiling.',
    body:
      'PwC: 30–35% of Americans are interested in GLP-1s. J.P. Morgan projects 25M U.S. users by 2030. The November 2025 Novo Nordisk + Eli Lilly direct deal priced branded medications at $245/month — less than half of prior pricing. Demand has never been larger. Access has not caught up.'
  },
  {
    n: '02',
    title: 'The compounding crackdown is reshaping the category.',
    body:
      'FDA Commissioner Makary announced a crackdown on non-FDA-approved GLP-1 compounding in February 2026. Brands built on regulatory arbitrage are restructuring. A venture launched on branded medications and senior clinical governance is already positioned for the post-crackdown world.'
  },
  {
    n: '03',
    title: 'Incumbents cannot serve what they cannot reach.',
    body:
      'Hims, Ro, Sequence, Mochi, Calibrate — every incumbent is English-only, form-driven, designed for affluent White consumers. The 25–30 million underserved adults with metabolic burden who cannot or will not engage those platforms are available to VitaMD from Day 1.'
  }
];

const COMPETITION_ROWS = [
  { feature: 'Voice AI intake',              hims: false, vita: true },
  { feature: 'Spanish-language native',      hims: false, vita: true },
  { feature: 'Senior physician governance',  hims: false, vita: true },
  { feature: 'Branded GLP-1 pathway',        hims: true,  vita: true },
  { feature: 'Underserved-demo strategy',    hims: false, vita: true },
  { feature: 'Media + subscriber model',     hims: false, vita: true }
];

const BUILT = [
  'Alfred Voice AI — production-deployed, English + Spanish',
  'Programmatic ad engine — compliance review in progress',
  'ConsentGuard — TCPA consent platform (AWS Marketplace)',
  'LLM orchestration pipeline — Claude + Deepgram + ElevenLabs',
  'Physician advisory anchor confirmed — Dr. Shidham',
  'CMMC Level 2 SPRS 110/110 · CAGE 130K7',
  'Azure Gov hosting provisioned (HIPAA-eligible)'
];

export default function Home() {
  return (
    <div className="bg-vita-navy text-stone-100">
      {/* ─── Hero ─────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden border-b border-vita-deep">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-32 top-0 h-[560px] w-[560px] rounded-full bg-vita-accent/10 blur-3xl"
        />
        <div className="relative mx-auto max-w-6xl px-6 pt-20 pb-24 md:pt-28 md:pb-32">
          <p className="text-[11px] tracking-[0.32em] text-vita-accent uppercase font-medium">
            Mule&nbsp;&nbsp;Works&nbsp;&nbsp;Inc.
          </p>
          <h1 className="mt-8 font-serif leading-none tracking-tight text-7xl md:text-9xl">
            <span className="text-stone-50">Vita</span>
            <span className="text-vita-accent">MD</span>
          </h1>
          <div className="mt-6 h-[3px] w-32 bg-vita-accent" />
          <p className="mt-8 font-serif italic text-2xl md:text-3xl text-stone-50">
            Physician-led metabolic health for everyone.
          </p>
          <p className="mt-3 text-vita-accent text-sm tracking-wider uppercase">
            Voice AI · Clinical Governance · Underserved Demographics
          </p>
          <p className="mt-8 max-w-2xl text-stone-300 text-lg leading-relaxed">
            A physician-led telehealth platform built for the 25–30 million adults
            the incumbents structurally cannot reach — with voice AI as the wedge,
            senior clinical governance as the moat, and GLP-1 metabolic health as
            the beachhead into a $40&nbsp;billion market.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/betterhealth"
              className="group inline-flex items-center gap-2 rounded-md bg-vita-accent px-6 py-3 text-vita-navy font-medium hover:bg-vita-bright transition"
            >
              Talk to Alfred
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/media"
              className="inline-flex items-center gap-2 rounded-md border border-vita-accent/30 px-6 py-3 text-stone-200 hover:border-vita-accent hover:text-vita-accent transition"
            >
              Watch the podcast
            </Link>
          </div>
        </div>
      </section>

      {/* ─── The Problem ─────────────────────────────────────────── */}
      <section className="border-b border-vita-deep">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-24">
          <p className="text-[11px] tracking-[0.32em] text-stone-500 uppercase">The problem</p>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl text-stone-50 leading-tight">
            Obesity is the defining health crisis of our time.
          </h2>
          <p className="mt-3 font-serif italic text-xl text-stone-400 max-w-3xl">
            And the people hit hardest are the least served by the current telehealth system.
          </p>

          <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            <Stat big="126M" label="U.S. adults projected obese by 2035." />
            <Stat big="49.9%" label="Obesity prevalence among Black adults." />
            <Stat big="45.6%" label="Obesity prevalence among Hispanic adults." />
            <Stat big="25–30M" label="Underserved adults with no real telehealth option." />
          </div>

          <div className="mt-14">
            <p className="text-[11px] tracking-[0.32em] text-stone-500 uppercase mb-5">The access gap</p>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <AccessRow
                lead="35% less likely"
                body="Hispanic adults use telehealth vs. White adults — despite higher disease burden."
              />
              <AccessRow
                lead="10× higher friction"
                body="Spanish-speaking patients face 10× higher friction logging onto telehealth platforms."
              />
              <AccessRow
                lead="43% more likely"
                body="Black men use emergency departments for usual care vs. continuous-care relationships."
              />
              <AccessRow
                lead="English-only design"
                body="Every major incumbent — Hims, Ro, Sequence, Mochi, Calibrate — was built by and for English-first users."
              />
              <AccessRow
                lead="Form-driven intake"
                body="Text-form platforms exclude patients who would talk but won’t type. Millions unreachable."
              />
              <AccessRow
                lead="No senior governance"
                body="White-label prescribing networks produce 85%+ inappropriate prescribing rates. Regulatory pressure intensifying."
              />
            </div>
          </div>
        </div>
      </section>

      {/* ─── Why Now ─────────────────────────────────────────────── */}
      <section className="border-b border-vita-deep bg-vita-deep/40">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-24">
          <p className="text-[11px] tracking-[0.32em] text-stone-500 uppercase">Why now</p>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl text-stone-50 leading-tight">
            Three forces are converging in 2026 to create VitaMD’s window.
          </h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {WHY_NOW.map((w) => (
              <div
                key={w.n}
                className="rounded-lg border border-stone-800 bg-vita-navy/60 p-6"
              >
                <div className="font-serif text-3xl text-vita-accent">{w.n}</div>
                <h3 className="mt-3 font-serif text-xl text-stone-50 leading-tight">{w.title}</h3>
                <p className="mt-3 text-stone-300 text-sm leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Solution (3 Pillars) ────────────────────────────────── */}
      <section className="border-b border-vita-deep">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-24">
          <p className="text-[11px] tracking-[0.32em] text-stone-500 uppercase">The solution</p>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl text-stone-50 leading-tight">
            <span>Vita</span><span className="text-vita-accent">MD</span> is physician-led metabolic health —
            voice-first, multilingual, built for the people the incumbents cannot reach.
          </h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {PILLARS.map((p) => (
              <div
                key={p.title}
                className="rounded-lg border border-stone-800 bg-vita-deep/60 p-7 hover:border-vita-accent/60 transition"
              >
                <p.icon className="w-7 h-7 text-vita-accent" />
                <h3 className="mt-5 font-serif text-2xl text-stone-50">{p.title}</h3>
                <p className="mt-3 text-stone-300 text-sm leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── The Product (5 Steps) ───────────────────────────────── */}
      <section id="how-it-works" className="border-b border-vita-deep">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-24">
          <p className="text-[11px] tracking-[0.32em] text-stone-500 uppercase">The product</p>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl text-stone-50 leading-tight">
            What a patient experiences with VitaMD.
          </h2>
          <ol className="mt-12 space-y-6">
            {PRODUCT_STEPS.map((s) => (
              <li
                key={s.n}
                className="flex gap-6 rounded-lg border border-stone-800 bg-vita-deep/60 p-6"
              >
                <div className="flex-shrink-0">
                  <div className="font-serif text-3xl text-vita-accent">{s.n}</div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <s.icon className="w-5 h-5 text-stone-400" />
                    <h3 className="font-serif text-2xl text-stone-50">{s.title}</h3>
                  </div>
                  <p className="mt-2 text-stone-400 leading-relaxed">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ─── Demographic Moat ────────────────────────────────────── */}
      <section className="border-b border-vita-deep bg-vita-deep/40">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-24">
          <p className="text-[11px] tracking-[0.32em] text-stone-500 uppercase">Demographic moat</p>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl text-stone-50 leading-tight">
            The highest disease burden. The lowest telehealth utilization.
          </h2>
          <p className="mt-4 max-w-3xl text-stone-300 text-lg">
            Incumbents haven’t closed this gap. VitaMD is purpose-built for it.
          </p>

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <Stat big="47–49M" label="U.S. Hispanic adults · 45.6% obesity prevalence" small />
            <Stat big="33M" label="U.S. Black adults · 49.9–56.9% obesity prevalence" small />
            <Stat big="13–16M" label="Limited-English-proficiency Hispanic adults · 35% less likely to use telehealth" small />
            <Stat big="25–30M" label="Underserved metabolic TAM · addressable from Day 1" small />
          </div>
        </div>
      </section>

      {/* ─── Competition ────────────────────────────────────────── */}
      <section className="border-b border-vita-deep">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-24">
          <p className="text-[11px] tracking-[0.32em] text-stone-500 uppercase">Competition</p>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl text-stone-50 leading-tight">
            We compete where incumbents cannot follow.
          </h2>

          <div className="mt-10 overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-stone-800 text-left text-sm">
                  <th className="py-3 pr-4 font-medium text-stone-400"></th>
                  <th className="py-3 px-4 font-medium text-stone-400 text-center">Hims / Ro</th>
                  <th className="py-3 px-4 font-medium text-vita-accent text-center">VitaMD</th>
                </tr>
              </thead>
              <tbody>
                {COMPETITION_ROWS.map((r) => (
                  <tr key={r.feature} className="border-b border-stone-900">
                    <td className="py-4 pr-4 text-stone-200">{r.feature}</td>
                    <td className="py-4 px-4 text-center">
                      {r.hims
                        ? <Check className="w-5 h-5 text-stone-500 inline" />
                        : <X className="w-5 h-5 text-stone-700 inline" />}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {r.vita
                        ? <Check className="w-5 h-5 text-vita-accent inline" />
                        : <X className="w-5 h-5 text-stone-700 inline" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ─── Team / About ───────────────────────────────────────── */}
      <section className="border-b border-vita-deep bg-vita-deep/40">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-24">
          <p className="text-[11px] tracking-[0.32em] text-stone-500 uppercase">The team</p>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl text-stone-50 leading-tight">
            Built by Brian Southers &amp; Mule Works.
          </h2>
          <div className="mt-10 grid gap-12 md:grid-cols-3">
            <div className="md:col-span-2 space-y-5 text-stone-300 leading-relaxed">
              <p>
                VitaMD is a Mule Works Inc. company, founded by Brian Southers — 25+ years
                across technology, education, and production, with 4 production AI platforms
                shipped. Mule Works is a Houston firm building applied AI for healthcare
                and defense (CAGE 130K7 · CMMC Level 2 SPRS 110/110).
              </p>
              <p>
                The intake voice at{' '}
                <Link to="/start" className="text-vita-accent hover:underline">
                  /start
                </Link>{' '}
                is <span className="text-stone-100 font-medium">Alfred Voice AI</span> — a
                concierge phone agent built in-house. Alfred is not a doctor. Every
                prescription, every protocol, every clinical decision is made by a licensed
                U.S. physician under standards set by the VitaMD medical advisory board,
                anchored by <span className="text-stone-100 font-medium">Dr. Ganesh Shidham</span>
                {' '}(Nephrology · OSU Wexner Medical Center).
              </p>
            </div>

            <div className="rounded-lg border border-stone-800 bg-vita-navy/60 p-6">
              <p className="text-[11px] tracking-[0.24em] text-vita-accent uppercase">
                Already built
              </p>
              <ul className="mt-4 space-y-2 text-sm text-stone-300">
                {BUILT.map((b) => (
                  <li key={b} className="flex gap-2">
                    <Check className="w-4 h-4 text-vita-accent flex-shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-12 flex items-center gap-4">
            <img
              src="/team/brian-southers.jpg"
              alt="Brian Southers"
              className="h-16 w-16 rounded-full object-cover ring-2 ring-vita-accent/40"
            />
            <div className="text-sm text-stone-400">
              <p>
                <span className="text-stone-200 font-medium">Brian Southers</span>
                {' '}— Founder &amp; CEO
              </p>
              <a
                href="mailto:brian@muleworks.co"
                className="text-vita-accent hover:text-vita-bright transition"
              >
                brian@muleworks.co
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Final CTA ──────────────────────────────────────────── */}
      <section className="border-b border-vita-deep">
        <div className="mx-auto max-w-4xl px-6 py-24 md:py-32 text-center">
          <h2 className="font-serif text-4xl md:text-6xl text-stone-50 leading-tight">
            Ready to talk to a doctor — not a form?
          </h2>
          <p className="mt-6 text-stone-400 text-lg max-w-2xl mx-auto">
            Pick a voice. Get a callback in 5 minutes. A real conversation in English or
            Spanish — no forms, no waiting room.
          </p>
          <div className="mt-10">
            <Link
              to="/betterhealth"
              className="group inline-flex items-center gap-2 rounded-md bg-vita-accent px-8 py-4 text-vita-navy font-medium text-lg hover:bg-vita-bright transition"
            >
              Talk to Alfred
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ big, label, small }) {
  return (
    <div>
      <div className={`font-serif text-vita-accent ${small ? 'text-4xl md:text-5xl' : 'text-6xl'}`}>
        {big}
      </div>
      <p className={`mt-3 text-stone-400 leading-relaxed ${small ? 'text-sm' : 'text-base'}`}>
        {label}
      </p>
    </div>
  );
}

function AccessRow({ lead, body }) {
  return (
    <div className="rounded-lg border border-stone-800 bg-vita-deep/40 p-4">
      <p className="font-serif text-lg text-vita-accent">{lead}</p>
      <p className="mt-1 text-stone-400 text-sm leading-relaxed">{body}</p>
    </div>
  );
}
